import React, { memo, useState, useCallback, useEffect } from "react";
import styles from "./FindAppliances.module.scss";
import { toast } from "react-toastify";
import FormWrapper from "../../../../../../common/components/form-wrapper/FormWrapper";
// import ApplianceService from "../../../../../../httpService/ApplianceService";
import { Appliance } from "../../../../../../model/appliance";
import { ApplianceItem } from "../../../../../../model/solar-recommendation-form/appliance-item";
import SearchResultItem from "./SearchResultItem";
import { useLazyGetApplianceCategoriesQuery } from "../../../../../../service/appliance-category";
import { CombinedAppliance } from "../../../../../../model/combined-appliance-category";
import SearchResultCategoryItem from "./SearchResultCategoryItem";

type EditApplianceProps = {
	applianceToChange: ApplianceItem | undefined;
	onEditAppliance: (appliance: ApplianceItem) => void;
};

type AddAplianceProps = {
	onAddAppliances: (appliances: Appliance[]) => void;
};

type Props = {
	selectedAppliancesFromBefore: Appliance[];
	editApplianceProps?: EditApplianceProps;
	addApplianceProps?: AddAplianceProps;
	onGetApplianceCount?: (number: number) => void;
	onBack: () => void;
};

const FindAppliances: React.FC<Props> = memo((props: Props) => {
	const { selectedAppliancesFromBefore, onBack, onGetApplianceCount, addApplianceProps, editApplianceProps } = props;

	const [search, setSearch] = useState<string>("");
	const [debouncedSearch, setDebouncedSearch] = useState<string>("");
	const [appliances, setAppliances] = useState<CombinedAppliance[]>([]);
	const [selectedAppliances, setSelectedAppliances] = useState<Appliance[]>([]);
    const [getApplianceCategories, { data: categories = [] }] = useLazyGetApplianceCategoriesQuery();


	useEffect(() => {
		const fetchAndCombineData = async () => {
			try {
				await getApplianceCategories();
				if (categories) {
					const defaultCategory = categories.find(cat => cat.name.toLowerCase() === "others");
					const otherCategories = categories.filter(cat => cat.name.toLowerCase() !== "others");

					const defaultAppliances =
						defaultCategory?.appliances.map(
							appliance =>
								new CombinedAppliance({
									id: appliance.id,
									name: appliance.name,
									power: appliance.power,
									iconUrl: appliance.iconUrl,
									applianceCategoryId: appliance.applianceCategoryId,
									applianceCategory: appliance.applianceCategory,
								})
						) || [];

					const categoryAppliances = otherCategories.map(category => {
						const sortedAppliances = [...category.appliances].sort((a, b) => a.name.localeCompare(b.name));

						return new CombinedAppliance({
							id: category.id,
							name: category.name,
							imageUrl: category.imageUrl,
							description: category.description,
							deleted: category.deleted,
							appliances: sortedAppliances,
						});
					});

					const combinedItems = sortAppliancesByName([...defaultAppliances, ...categoryAppliances]);

					setAppliances(combinedItems);
					if (onGetApplianceCount) onGetApplianceCount(combinedItems.length);
				}
			} catch (error) {
				toast.error("There was an error fetching the appliances and categories");
			}
		};

		fetchAndCombineData();
	}, [categories, getApplianceCategories, onGetApplianceCount]);

	const getFilteredAppliances = useCallback(() => {
		if (!debouncedSearch) return appliances;
	
		const searchTerm = debouncedSearch.toLowerCase();
	
		return appliances.filter(item => {
			if (item.isCategory()) {
				const categoryData = item.toApplianceCategory();
				const categoryMatches = categoryData.name.toLowerCase().includes(searchTerm);
				const hasMatchingAppliances = categoryData.appliances?.some(
					appliance => appliance.name.toLowerCase().includes(searchTerm)
				);
				return categoryMatches || hasMatchingAppliances;
			} else {
				return item.name.toLowerCase().includes(searchTerm);
			}
		});
	}, [appliances, debouncedSearch]);

	const sortAppliancesByName = (items: CombinedAppliance[]): CombinedAppliance[] => {
		return [...items].sort((a, b) => {
			if (a.isCategory() && !b.isCategory()) return -1;
			if (!a.isCategory() && b.isCategory()) return 1;

			return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
		});
	};

	function containsSearch() {
		if (debouncedSearch) {
			return styles.searchContainesText;
		}
	}

	function getCategorySelectionState(category: CombinedAppliance): boolean {
		const categoryData = category.toApplianceCategory();
		const appliances = categoryData.appliances || [];
		return appliances.some(appliance => 
			selectedAppliancesFromBefore.some(selectedAppliance => 
				selectedAppliance.id === appliance.id
			)
		);
	}

	function addAplianceToSelected(appliance: Appliance) {
        if (!selectedAppliancesFromBefore.some(selected => selected.id === appliance.id)) {
            setSelectedAppliances(prev => [...prev, appliance]);
        }
    }
	function removeApplianceFromSelected(appliance: Appliance) {
        setSelectedAppliances(prev => 
            prev.filter(selectedAppliance => selectedAppliance.id !== appliance.id)
        );
    }

	function changeSearch(e: React.ChangeEvent<HTMLInputElement>) {
		setSearch(e.target.value);
	}

	function existsInSelected(appliance: Appliance) {
        return selectedAppliances.some(selected => selected.id === appliance.id) ||
               selectedAppliancesFromBefore.some(selected => selected.id === appliance.id);
    }

	function isApplianceSelected(appliance: Appliance) {
		return selectedAppliancesFromBefore.some(selectedAppliance => selectedAppliance.id === appliance.id);
	}

	function addApplicances() {
		addApplianceProps!.onAddAppliances(selectedAppliances);
	}

	function passOptionalPropsToFindAppliances(appliance: Appliance) {
		if (!editApplianceProps) {
			return {
				addingProps: {
					isSelected: existsInSelected(appliance),
					onAddAppliance: addAplianceToSelected,
					onRemoveAppliance: removeApplianceFromSelected,
				},
			};
		} else {
			return {
				editingProps: {
					appliance: editApplianceProps.applianceToChange!,
					onEditAppliance: editApplianceProps.onEditAppliance,
				},
			};
		}
	}

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearch(search);
		}, 500);

		return () => clearTimeout(timer);
	}, [search]);

	return (
		<FormWrapper
			titleSection={{ title: "New Appliance", subtitle: "Search and select an appliance to continue.", buttonTitle: "Proceed" }}
			onBack={onBack}
			onSaveAndContinue={addApplicances}
		>
			<div className={styles.findApplianceContainer}>
				<div className={`${styles.searchContainer} ${containsSearch()}`}>
					<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path
							d="M15.2825 14.7176L12.5001 11.9576C13.5801 10.6109 14.1032 8.90152 13.9617 7.18103C13.8201 5.46053 13.0248 3.85964 11.7392 2.70753C10.4536 1.55543 8.77541 0.939683 7.04974 0.986903C5.32408 1.03412 3.68209 1.74072 2.46141 2.96141C1.24072 4.18209 0.534124 5.82408 0.486903 7.54974C0.439683 9.27541 1.05543 10.9536 2.20753 12.2392C3.35964 13.5248 4.96053 14.3201 6.68103 14.4617C8.40152 14.6032 10.1109 14.0801 11.4576 13.0001L14.2176 15.7601C14.2873 15.8303 14.3702 15.8861 14.4616 15.9242C14.553 15.9623 14.651 15.9819 14.7501 15.9819C14.8491 15.9819 14.9471 15.9623 15.0385 15.9242C15.1299 15.8861 15.2128 15.8303 15.2825 15.7601C15.4177 15.6202 15.4933 15.4333 15.4933 15.2388C15.4933 15.0443 15.4177 14.8574 15.2825 14.7176ZM7.25005 13.0001C6.2117 13.0001 5.19666 12.6921 4.33331 12.1153C3.46995 11.5384 2.79704 10.7185 2.39968 9.75914C2.00232 8.79983 1.89836 7.74423 2.10093 6.72583C2.3035 5.70743 2.80351 4.77197 3.53774 4.03774C4.27197 3.30351 5.20743 2.8035 6.22583 2.60093C7.24423 2.39836 8.29983 2.50232 9.25914 2.89968C10.2185 3.29704 11.0384 3.96995 11.6153 4.83331C12.1921 5.69666 12.5001 6.7117 12.5001 7.75005C12.5001 9.14244 11.9469 10.4778 10.9624 11.4624C9.97779 12.4469 8.64244 13.0001 7.25005 13.0001Z"
							fill="#2E3336"
						/>
					</svg>
					<input placeholder="Search amenities" onChange={changeSearch} />
				</div>
				<div className={styles.searchResultContainer}>
					{getFilteredAppliances().map((item, index) => {
						if (item.isCategory()) {
							const wasSelected = getCategorySelectionState(item);
							return (
								<SearchResultCategoryItem
									key={index}
									category={item}
									isAdded={wasSelected}
									selectedAppliances={selectedAppliances}
									previouslySelectedAppliances={selectedAppliancesFromBefore}
									addingProps={{
										isSelected: false,
										onAddAppliance: addAplianceToSelected,
										onRemoveAppliance: removeApplianceFromSelected,
									}}
								/>
							);
						} else {
							return (
								<SearchResultItem
									key={index}
									appliance={item.toAppliance()}
									isAdded={isApplianceSelected(item.toAppliance())}
									{...passOptionalPropsToFindAppliances(item.toAppliance())}
								/>
							);
						}
					})}
				</div>
			</div>
		</FormWrapper>
	);
});

export default FindAppliances;
